/**
 * ChatGapInput — free-form input shown when the user clicks ✗ on a
 * ChatGapChip ("Different industry") or "Tell me the {field}".
 *
 * Submits a single line; Enter accepts, Escape cancels (returns to chips).
 * The submitted value flows back as a regular user message via the
 * onSubmit callback wired in ChatMessage.tsx.
 */
import { useEffect, useRef, useState } from 'react';

interface Props {
  field: string;
  onSubmit: (value: string) => void;
  onCancel: () => void;
}

function humanizeField(field: string): string {
  return field.replace(/_/g, ' ');
}

export function ChatGapInput({ field, onSubmit, onCancel }: Props) {
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const submit = () => {
    const trimmed = value.trim();
    if (!trimmed) {return;}
    onSubmit(trimmed);
  };

  return (
    <div className="mt-2 flex items-stretch gap-2">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            submit();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
          }
        }}
        placeholder={`Enter the ${humanizeField(field)}`}
        className="flex-1 border border-border bg-surface-main px-3 py-1 text-body-sm text-txt-primary placeholder:text-txt-tertiary focus:border-orange focus:outline-none"
      />
      <button
        type="button"
        onClick={submit}
        disabled={!value.trim()}
        className="bg-orange px-3 py-1 text-body-sm font-semibold text-white transition-opacity disabled:opacity-50"
      >
        OK
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="border border-border bg-surface-card px-3 py-1 text-body-sm text-txt-secondary hover:border-border-strong hover:text-txt-primary"
      >
        Cancel
      </button>
    </div>
  );
}
